/**
 * ============================================
 * Theme Core Utilities - 优化版本
 * 模块化、高性能、易维护
 * ============================================
 */

;(function(window, document, undefined) {
  'use strict';

  // ============================================
  // 1. 工具函数
  // ============================================

  const Utils = {
    /**
     * 安全执行函数
     */
    safeExecute(fn, fallback = null) {
      try {
        return fn();
      } catch (error) {
        if (window.console) {
          console.warn('Safe execute error:', error);
        }
        return fallback;
      }
    },

    /**
     * 防抖 - 延迟执行
     */
    debounce(fn, wait = 250) {
      let timer;
      return function(...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    /**
     * 节流 - 限制执行频率
     */
    throttle(fn, limit = 100) {
      let inThrottle = false;
      return function(...args) {
        if (!inThrottle) {
          fn.apply(this, args);
          inThrottle = true;
          setTimeout(() => { inThrottle = false; }, limit);
        }
      };
    },

    /**
     * 获取请求配置
     */
    fetchConfig(type = 'json') {
      return {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': `application/${type}`
        }
      };
    },

    /**
     * 获取容器内所有可聚焦元素
     */
    getFocusableElements(container) {
      const selector = [
        'summary',
        'a[href]',
        'button:enabled',
        '[tabindex]:not([tabindex^="-"])',
        '[draggable]',
        'area',
        'input:not([type=hidden]):enabled',
        'select:enabled',
        'textarea:enabled',
        'object',
        'iframe',
        '[role="button"]',
        '[role="radio"]',
        '[role="checkbox"]'
      ].join(', ');
      return Array.from(container.querySelectorAll(selector));
    },

    /**
     * 检测是否为导航键
     */
    isNavKey(code) {
      return this.NAV_KEYS.includes(code);
    },

    /**
     * 暂停所有媒体
     */
    pauseAllMedia() {
      // YouTube
      document.querySelectorAll('.js-youtube').forEach(video => {
        if (video.contentWindow) {
          video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        }
      });

      // Vimeo
      document.querySelectorAll('.js-vimeo').forEach(video => {
        if (video.contentWindow) {
          video.contentWindow.postMessage('{"method":"pause"}', '*');
        }
      });

      // HTML5 Video
      document.querySelectorAll('video').forEach(video => {
        if (typeof video.pause === 'function') {
          video.pause();
        }
      });

      // Product Model
      document.querySelectorAll('product-model').forEach(model => {
        if (model.modelViewerUI && typeof model.modelViewerUI.pause === 'function') {
          model.modelViewerUI.pause();
        }
      });
    }
  };

  // ============================================
  // 2. 键盘常量
  // ============================================

  const KEY_CODES = {
    TAB: 'TAB',
    ESCAPE: 'ESCAPE',
    ENTER: 'ENTER',
    SPACE: 'SPACE',
    ARROWUP: 'ARROWUP',
    ARROWDOWN: 'ARROWDOWN',
    ARROWLEFT: 'ARROWLEFT',
    ARROWRIGHT: 'ARROWRIGHT',
    HOME: 'HOME',
    END: 'END',
    PAGEUP: 'PAGEUP',
    PAGEDOWN: 'PAGEDOWN'
  };

  Utils.KEY_CODES = KEY_CODES;
  Utils.NAV_KEYS = Object.values(KEY_CODES);

  // ============================================
  // 3. 焦点管理
  // ============================================

  const FocusManager = {
    _handlers: {},

    /**
     * 锁定焦点在容器内
     */
    trap(container, elementToFocus = container) {
      const elements = Utils.getFocusableElements(container);
      if (elements.length === 0) return;

      const first = elements[0];
      const last = elements[elements.length - 1];

      this.remove();

      this._handlers.focusin = (event) => {
        if (!container.contains(event.target)) return;
        document.addEventListener('keydown', this._handlers.keydown);
      };

      this._handlers.focusout = () => {
        document.removeEventListener('keydown', this._handlers.keydown);
      };

      this._handlers.keydown = (event) => {
        if (event.code !== KEY_CODES.TAB) return;

        if (event.target === last && !event.shiftKey) {
          event.preventDefault();
          first.focus();
        }

        if ((event.target === container || event.target === first) && event.shiftKey) {
          event.preventDefault();
          last.focus();
        }
      };

      document.addEventListener('focusout', this._handlers.focusout);
      document.addEventListener('focusin', this._handlers.focusin);

      if (elementToFocus && typeof elementToFocus.focus === 'function') {
        elementToFocus.focus();
      }
    },

    /**
     * 移除焦点锁定
     */
    remove(elementToFocus = null) {
      document.removeEventListener('focusin', this._handlers.focusin);
      document.removeEventListener('focusout', this._handlers.focusout);
      document.removeEventListener('keydown', this._handlers.keydown);

      if (elementToFocus && typeof elementToFocus.focus === 'function') {
        elementToFocus.focus();
      }
    }
  };

  // ============================================
  // 4. Escape键处理
  // ============================================

  function handleEscapeKey(event) {
    if (event.code !== KEY_CODES.ESCAPE) return;

    const openDetails = event.target.closest('details[open]');
    if (!openDetails) return;

    const summary = openDetails.querySelector('summary');
    openDetails.removeAttribute('open');
    if (summary) {
      summary.setAttribute('aria-expanded', 'false');
      summary.focus();
    }
  }

  // ============================================
  // 5. Focus Visible Polyfill
  // ============================================

  function initFocusVisible() {
    let currentFocused = null;
    let mouseClick = false;
    let keyboardUsed = false;

    const resetMouseState = () => {
      setTimeout(() => {
        if (!keyboardUsed) mouseClick = false;
      }, 100);
    };

    window.addEventListener('keydown', (event) => {
      if (Utils.isNavKey(event.code)) {
        mouseClick = false;
        keyboardUsed = true;
      }
    });

    window.addEventListener('mousedown', () => {
      mouseClick = true;
      keyboardUsed = false;
    });

    window.addEventListener('click', resetMouseState);

    window.addEventListener('focus', () => {
      if (currentFocused) {
        currentFocused.classList.remove('focused');
      }

      if (mouseClick) return;

      currentFocused = document.activeElement;
      if (currentFocused && currentFocused !== document.body) {
        currentFocused.classList.add('focused');
      }
    }, true);
  }

  // ============================================
  // 6. Details Summary 初始化
  // ============================================

  function initDetailsSummary() {
    document.querySelectorAll('[id^="Details-"] summary').forEach(summary => {
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', 'false');

      const sibling = summary.nextElementSibling;
      if (sibling?.id) {
        summary.setAttribute('aria-controls', sibling.id);
      }

      summary.addEventListener('click', (event) => {
        const details = event.currentTarget.closest('details');
        const isOpen = details?.hasAttribute('open');
        event.currentTarget.setAttribute('aria-expanded', !isOpen);
      });

      if (!summary.closest('header-drawer')) {
        summary.parentElement.addEventListener('keyup', handleEscapeKey);
      }
    });
  }

  // ============================================
  // 7. Quantity Input Component
  // ============================================

  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.changeEvent = new Event('change', { bubbles: true });

      this.querySelectorAll('button').forEach(button => {
        button.addEventListener('click', this._onButtonClick.bind(this));
      });
    }

    _onButtonClick(event) {
      event.preventDefault();
      if (!this.input) return;

      const previousValue = this.input.value;
      const isPlus = event.target.name === 'plus';
      isPlus ? this.input.stepUp() : this.input.stepDown();

      if (previousValue !== this.input.value) {
        this.input.dispatchEvent(this.changeEvent);
      }
    }
  }

  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', QuantityInput);
  }

  // ============================================
  // 8. Modal Dialog
  // ============================================

  class ModalDialog extends HTMLElement {
    constructor() {
      super();
      this._openedBy = null;

      const closeBtn = this.querySelector('[id^="ModalClose-"]');
      if (closeBtn) {
        closeBtn.addEventListener('click', this.hide.bind(this));
      }

      this.addEventListener('keyup', (event) => {
        if (event.code === KEY_CODES.ESCAPE) this.hide();
      });

      this._initCloseEvents();
    }

    _initCloseEvents() {
      if (this.classList.contains('media-modal')) {
        this.addEventListener('pointerup', (event) => {
          if (event.pointerType === 'mouse' &&
              !event.target.closest('deferred-media') &&
              !event.target.closest('product-model')) {
            this.hide();
          }
        });
      } else {
        this.addEventListener('click', (event) => {
          if (event.target.nodeName === 'MODAL-DIALOG') {
            this.hide();
          }
        });
      }
    }

    connectedCallback() {
      if (this._moved) return;
      this._moved = true;
      document.body.appendChild(this);
    }

    show(opener) {
      this._openedBy = opener;

      const popup = this.querySelector('.template-popup');
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');

      if (popup?.loadContent) {
        popup.loadContent();
      }

      const dialog = this.querySelector('[role="dialog"]');
      if (dialog) {
        FocusManager.trap(this, dialog);
      }

      Utils.pauseAllMedia();
    }

    hide() {
      document.body.classList.remove('overflow-hidden');
      this.removeAttribute('open');

      if (this._openedBy) {
        FocusManager.remove(this._openedBy);
      }

      Utils.pauseAllMedia();
    }
  }

  if (!customElements.get('modal-dialog')) {
    customElements.define('modal-dialog', ModalDialog);
  }

  // ============================================
  // 9. Modal Opener
  // ============================================

  class ModalOpener extends HTMLElement {
    constructor() {
      super();
      const button = this.querySelector('button');
      if (button) {
        button.addEventListener('click', () => {
          const modal = document.querySelector(this.getAttribute('data-modal'));
          if (modal?.show) {
            modal.show(button);
          }
        });
      }
    }
  }

  if (!customElements.get('modal-opener')) {
    customElements.define('modal-opener', ModalOpener);
  }

  // ============================================
  // 10. Deferred Media
  // ============================================

  class DeferredMedia extends HTMLElement {
    constructor() {
      super();
      const poster = this.querySelector('[id^="Deferred-Poster-"]');
      if (poster) {
        poster.addEventListener('click', this.loadContent.bind(this));
      }
    }

    loadContent(focus = true) {
      Utils.pauseAllMedia();

      if (this.hasAttribute('loaded')) return;

      const template = this.querySelector('template');
      if (!template) return;

      const clone = template.content.firstElementChild;
      if (!clone) return;

      const content = document.createElement('div');
      content.appendChild(clone.cloneNode(true));
      this.setAttribute('loaded', 'true');

      const media = content.querySelector('video, model-viewer, iframe');
      if (media) {
        this.appendChild(media);
        if (focus && typeof media.focus === 'function') {
          media.focus();
        }
      }
    }
  }

  if (!customElements.get('deferred-media')) {
    customElements.define('deferred-media', DeferredMedia);
  }

  // ============================================
  // 11. Menu Drawer
  // ============================================

  class MenuDrawer extends HTMLElement {
    constructor() {
      super();
      this.mainDetailsToggle = this.querySelector('details');

      // iPhone 视口高度修复
      if (navigator.platform === 'iPhone') {
        document.documentElement.style.setProperty(
          '--viewport-height',
          `${window.innerHeight}px`
        );
      }

      this._bindEvents();
    }

    _bindEvents() {
      this.addEventListener('keyup', this._onKeyUp.bind(this));
      this.addEventListener('focusout', this._onFocusOut.bind(this));

      this.querySelectorAll('summary').forEach(summary => {
        summary.addEventListener('click', this._onSummaryClick.bind(this));
      });

      this.querySelectorAll('button').forEach(button => {
        button.addEventListener('click', this._onCloseButtonClick.bind(this));
      });
    }

    _onKeyUp(event) {
      if (event.code !== KEY_CODES.ESCAPE) return;

      const openDetails = event.target.closest('details[open]');
      if (!openDetails) return;

      if (openDetails === this.mainDetailsToggle) {
        this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
      } else {
        this._closeSubmenu(openDetails);
      }
    }

    _onSummaryClick(event) {
      const summary = event.currentTarget;
      const details = summary.parentNode;
      const isOpen = details.hasAttribute('open');

      if (details === this.mainDetailsToggle) {
        if (isOpen) event.preventDefault();
        if (isOpen) {
          this.closeMenuDrawer(event, summary);
        } else {
          this._openMenuDrawer(summary);
        }
      } else {
        setTimeout(() => {
          details.classList.add('menu-opening');
          summary.setAttribute('aria-expanded', 'true');
          this._addTrapFocusToSubmenu(details);
        }, 100);
      }
    }

    _addTrapFocusToSubmenu(details) {
      const summary = details.querySelector('summary');
      const nextSibling = summary?.nextElementSibling;
      if (!nextSibling) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const addFocus = () => {
        FocusManager.trap(nextSibling, details.querySelector('button'));
        nextSibling.removeEventListener('transitionend', addFocus);
      };

      if (!reducedMotion || reducedMotion.matches) {
        addFocus();
      } else {
        nextSibling.addEventListener('transitionend', addFocus);
      }
    }

    _openMenuDrawer(summary) {
      requestAnimationFrame(() => {
        this.mainDetailsToggle.classList.add('menu-opening');
      });

      summary.setAttribute('aria-expanded', 'true');
      FocusManager.trap(this.mainDetailsToggle, summary);
      document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    }

    closeMenuDrawer(event, elementToFocus = false) {
      if (!event) return;

      this.mainDetailsToggle.classList.remove('menu-opening');
      this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });

      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
      FocusManager.remove(elementToFocus);
      this._closeAnimation(this.mainDetailsToggle);
    }

    _onFocusOut() {
      setTimeout(() => {
        if (this.mainDetailsToggle.hasAttribute('open') &&
            !this.mainDetailsToggle.contains(document.activeElement)) {
          this.closeMenuDrawer({});
        }
      });
    }

    _onCloseButtonClick(event) {
      const details = event.currentTarget.closest('details');
      this._closeSubmenu(details);
    }

    _closeSubmenu(details) {
      if (!details) return;

      details.classList.remove('menu-opening');
      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);

      const summary = details.querySelector('summary');
      if (summary) {
        summary.setAttribute('aria-expanded', 'false');
        FocusManager.remove(summary);
      }

      this._closeAnimation(details);
    }

    _closeAnimation(details) {
      let startTime;

      const animate = (time) => {
        if (!startTime) startTime = time;
        if (time - startTime < 400) {
          requestAnimationFrame(animate);
        } else {
          details.removeAttribute('open');
          const parent = details.closest('details[open]');
          if (parent) {
            FocusManager.trap(parent, parent.querySelector('summary'));
          }
        }
      };

      requestAnimationFrame(animate);
    }
  }

  if (!customElements.get('menu-drawer')) {
    customElements.define('menu-drawer', MenuDrawer);
  }

  // ============================================
  // 12. Header Drawer
  // ============================================

  class HeaderDrawer extends MenuDrawer {
    constructor() {
      super();
      this.header = document.getElementById('shopify-section-header');
      this.borderOffset = 0;

      if (this.closest('.header-wrapper')?.classList.contains('header-wrapper--border-bottom')) {
        this.borderOffset = 1;
      }
    }

    _openMenuDrawer(summary) {
      if (this.header) {
        const bottomPos = parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset);
        document.documentElement.style.setProperty('--header-bottom-position', `${bottomPos}px`);
        this.header.classList.add('menu-open');
      }

      super._openMenuDrawer(summary);
    }

    closeMenuDrawer(event, elementToFocus) {
      super.closeMenuDrawer(event, elementToFocus);
      if (this.header) {
        this.header.classList.remove('menu-open');
      }
      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    }
  }

  if (!customElements.get('header-drawer')) {
    customElements.define('header-drawer', HeaderDrawer);
  }

  // ============================================
  // 13. Variant Selects (合并优化)
  // ============================================

  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this._variantData = null;
      this._currentVariant = null;
      this._options = [];
      this._cache = new Map();

      this.addEventListener('change', this._onVariantChange.bind(this));
    }

    // ---- 核心方法 ----
    _onVariantChange(event) {
      this._updateOptions();
      this._updateMasterId();

      if (!this._currentVariant) {
        this._setUnavailable();
        return;
      }

      this._updateMedia();
      this._updateURL();
      this._updateVariantInput();
      this._renderProductInfo();
      this._updateShareUrl();
      this._updatePickupAvailability();
      this._removeErrorMessage();
    }

    _updateOptions() {
      this._options = Array.from(this.querySelectorAll('select')).map(el => el.value);
    }

    _updateMasterId() {
      const variants = this._getVariantData();
      this._currentVariant = variants.find(variant => {
        return variant.options.every((option, index) => option === this._options[index]);
      });
    }

    _getVariantData() {
      if (!this._variantData) {
        const jsonEl = this.querySelector('[type="application/json"]');
        if (jsonEl) {
          this._variantData = Utils.safeExecute(() => JSON.parse(jsonEl.textContent), []);
        }
      }
      return this._variantData || [];
    }

    // ---- UI 更新 ----
    _updateMedia() {
      if (!this._currentVariant?.featured_media) return;

      const mediaId = this._currentVariant.featured_media.id;
      const selector = `[data-media-id="${this.dataset.section}-${this.dataset.product}-${mediaId}"]`;

      if (this.dataset.layout === 'card') {
        this._updateCardMedia(selector);
      } else {
        this._updateSliderMedia(this._currentVariant.featured_media.position - 1);
      }
    }

    _updateCardMedia(selector) {
      const card = this.closest('.card');
      if (!card) return;

      const newMedia = card.querySelector(selector);
      if (!newMedia) return;

      const parent = newMedia.parentElement;
      if (parent && parent.firstChild !== newMedia) {
        parent.prepend(newMedia);
      }

      const stickyHeader = document.querySelector('sticky-header');
      if (stickyHeader) {
        stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }
    }

    _updateSliderMedia(index) {
      const $slider = $('.pdp-main-slider');
      if ($slider.hasClass('slick-initialized')) {
        $slider.slick('slickGoTo', index);
      }
    }

    _updateURL() {
      if (!this._currentVariant || this.dataset.updateUrl === 'false') return;
      const url = `${this.dataset.url}?variant=${this._currentVariant.id}`;
      window.history.replaceState({}, '', url);
    }

    _updateVariantInput() {
      const selector = this.dataset.layout === 'card'
        ? `#ProductInfo-${this.dataset.section}-${this.dataset.product}, #product-form-installment`
        : `#product-form-${this.dataset.section}, #product-form-installment`;

      document.querySelectorAll(selector).forEach(form => {
        const input = form.querySelector('input[name="id"]');
        if (input) {
          input.value = this._currentVariant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    _renderProductInfo() {
      const url = `${this.dataset.url}?variant=${this._currentVariant.id}&section_id=${this.dataset.section}`;

      // 使用缓存避免重复请求
      if (this._cache.has(url)) {
        this._applyProductInfo(this._cache.get(url));
        return;
      }

      fetch(url)
        .then(res => res.text())
        .then(html => {
          this._cache.set(url, html);
          this._applyProductInfo(html);
        })
        .catch(error => {
          if (window.console) {
            console.warn('Error rendering product info:', error);
          }
        });
    }

    _applyProductInfo(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const priceId = `price-${this.dataset.section}`;

      const source = doc.getElementById(priceId);
      const destination = document.getElementById(priceId);

      if (source && destination) {
        destination.innerHTML = source.innerHTML;
        destination.classList.remove('visibility-hidden');
      }
    }

    _updateShareUrl() {
      const shareButton = document.getElementById(`Share-${this.dataset.section}`);
      if (shareButton?.updateUrl) {
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this._currentVariant.id}`);
      }
    }

    _updatePickupAvailability() {
      const element = document.querySelector('pickup-availability');
      if (!element?.fetchAvailability) return;

      if (this._currentVariant?.available) {
        element.fetchAvailability(this._currentVariant.id);
      } else {
        element.removeAttribute('available');
        element.innerHTML = '';
      }
    }

    _removeErrorMessage() {
      const section = this.closest('section');
      if (!section) return;

      const productForm = section.querySelector('product-form');
      if (productForm?.handleErrorMessage) {
        productForm.handleErrorMessage();
      }
    }

    _setUnavailable() {
      const selector = this.dataset.layout === 'card'
        ? `product-form-${this.dataset.section}-${this.dataset.product}`
        : `product-form-${this.dataset.section}`;

      const form = document.getElementById(selector);
      if (!form) return;

      const button = form.querySelector('[name="add"]');
      if (button) {
        const textSpan = button.querySelector('span');
        if (textSpan) {
          textSpan.textContent = window.variantStrings?.unavailable || 'Unavailable';
        }
      }

      const price = document.getElementById(`price-${this.dataset.section}`);
      if (price) {
        price.classList.add('visibility-hidden');
      }
    }
  }

  if (!customElements.get('variant-selects')) {
    customElements.define('variant-selects', VariantSelects);
  }

  // ============================================
  // 14. Variant Radios
  // ============================================

  class VariantRadios extends VariantSelects {
    _updateOptions() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this._options = fieldsets.map(fieldset => {
        const checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : '';
      });
    }
  }

  if (!customElements.get('variant-radios')) {
    customElements.define('variant-radios', VariantRadios);
  }

  // ============================================
  // 15. Shopify 全局对象
  // ============================================

  if (typeof window.Shopify === 'undefined') {
    window.Shopify = {};
  }

  // 绑定函数
  Shopify.bind = function(fn, scope) {
    return function() {
      return fn.apply(scope, arguments);
    };
  };

  // 按值设置选择器
  Shopify.setSelectorByValue = function(selector, value) {
    if (!selector?.options) return -1;
    for (let i = 0; i < selector.options.length; i++) {
      const option = selector.options[i];
      if (value === option.value || value === option.innerHTML) {
        selector.selectedIndex = i;
        return i;
      }
    }
    return -1;
  };

  // 添加事件监听
  Shopify.addListener = function(target, eventName, callback) {
    if (target.addEventListener) {
      target.addEventListener(eventName, callback, false);
    } else if (target.attachEvent) {
      target.attachEvent('on' + eventName, callback);
    }
  };

  // POST 链接
  Shopify.postLink = function(path, options = {}) {
    const method = options.method || 'post';
    const params = options.parameters || {};

    const form = document.createElement('form');
    form.method = method;
    form.action = path;

    Object.keys(params).forEach(key => {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = key;
      hidden.value = params[key];
      form.appendChild(hidden);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  // ============================================
  // 16. Country Province Selector
  // ============================================

  Shopify.CountryProvinceSelector = function(countryDomId, provinceDomId, options = {}) {
    this.countryEl = document.getElementById(countryDomId);
    this.provinceEl = document.getElementById(provinceDomId);
    this.provinceContainer = document.getElementById(options.hideElement || provinceDomId);

    if (this.countryEl) {
      Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));
      this.initCountry();
    }
    this.initProvince();
  };

  Shopify.CountryProvinceSelector.prototype = {
    initCountry() {
      const value = this.countryEl.getAttribute('data-default');
      Shopify.setSelectorByValue(this.countryEl, value);
      this.countryHandler();
    },

    initProvince() {
      if (!this.provinceEl) return;
      const value = this.provinceEl.getAttribute('data-default');
      if (value && this.provinceEl.options.length > 0) {
        Shopify.setSelectorByValue(this.provinceEl, value);
      }
    },

    countryHandler() {
      if (!this.countryEl || !this.provinceEl) return;

      const opt = this.countryEl.options[this.countryEl.selectedIndex];
      const raw = opt.getAttribute('data-provinces');
      const provinces = raw ? Utils.safeExecute(() => JSON.parse(raw), []) : [];

      this._clearOptions(this.provinceEl);

      if (provinces.length === 0) {
        if (this.provinceContainer) {
          this.provinceContainer.style.display = 'none';
        }
      } else {
        provinces.forEach(province => {
          const option = document.createElement('option');
          option.value = province[0];
          option.textContent = province[1];
          this.provinceEl.appendChild(option);
        });
        if (this.provinceContainer) {
          this.provinceContainer.style.display = '';
        }
      }
    },

    _clearOptions(selector) {
      while (selector.firstChild) {
        selector.removeChild(selector.firstChild);
      }
    }
  };

  // ============================================
  // 17. DOM 就绪初始化
  // ============================================

  function init() {
    // Details Summary
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initDetailsSummary);
    } else {
      initDetailsSummary();
    }

    // Focus Visible Polyfill
    Utils.safeExecute(() => {
      if (!document.querySelector(':focus-visible')) {
        initFocusVisible();
      }
    });

    // 挂载 pauseAllMedia 到 window
    if (typeof window.pauseAllMedia === 'undefined') {
      window.pauseAllMedia = Utils.pauseAllMedia;
    }
  }

  // ============================================
  // 18. 导出
  // ============================================

  if (typeof window.theme === 'undefined') {
    window.theme = {};
  }

  window.theme.utils = Utils;
  window.theme.focus = FocusManager;
  window.theme.keys = KEY_CODES;

  // 初始化
  init();

})(window, document);